import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import EligibilityOverviewContainer from './EligibilityOverviewContainer';
import EligibilityDetailsContainer from './EligibilityDetailsContainer';
import SeeksQHP from '@components/individualPortal/eligibilityResults/SeeksQHP';
import RequestFullDetermination from '@components/individualPortal/eligibilityResults/RequestFullDetermination';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { ALL_ELIGIBILITY_TYPES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { configs } from '@constants/configs/configs';
import { APP_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import {
    fetchEligibilityResults,
    saveSeekQHPChanges,
    postFullDetermination,
    setFullDetermination
} from '@actions/individualPortal/eligibilityResults/eligibilityResultsActionCreators';
import { checkVerificationPending, getEnvironment } from '@actions/common/actionCreators';
import { getFormattedHeaders } from '@utils/normalize/getFormattedHeaders';
import { executeCallbacks } from '@utils/miscellaneous/executeCallbacks';
import { preventerTools } from '@utils/tools/preventerTools';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';

// hooks
import useEnv from '@hooks/useEnv';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStateCode } from '@hooks/index';

const EligibilityResultsContainer = (props) => {

    const {
        checkVerificationPending,
        fetchEligibilityResults,
        getEnvironment,
        saveSeekQHPChanges,
        postFullDetermination,
        setFullDetermination,
        eligibilityResults,
        eligibilityResults: {
            applicationStatus,
            applicants,
            aptc,
            stateSubsidy,
            fullDeterminationRequested,
            encryptedCaseNumber,
            isLatestApplication,
            isFinancial,
            source
        },
        environment: {
            stateCode,
            isState,
            exchangeName,
            stateSubsidyEnabled,
            requestFullDetermination,
            showSeekQHPCollapsed = false,
            delayPopup,
            user: {
                role
            },
        }
    } = props;
    
    const { state } = useLocation(); 
    const navigate = useNavigate();

    const ELIGIBILITY_ALERT_TYPES = {
        SSAP_SUBMISSION: 'SSAP_SUBMISSION',
        SAVE_SEEKS_QHP: 'SAVE_SEEKS_QHP',
        REQUEST_DETERMINATION: 'REQUEST_DETERMINATION'
    };

    const FULL_DETERMINATION_CONFIG = {
        OFF: 'OFF',
        ONCE_PER_APP: 'ONCE_PER_APP',
        ON_APP_SUBMISSION_ONLY: 'ON_APP_SUBMISSION_ONLY'
    };

    const APP_SOURCE = {
        RR: 'RR',
    };

    const DISALLOWED_APP_SOURCES = [APP_SOURCE.RR];

    const { getEnv } = useEnv();
    const [additionalNote, setAdditionalNoteVisibility] = useState(false);
    const [allowedAppStatus, setAllowedAppStatus] = useState(false);
    const [isAllowedAppSource, setIsAllowedAppSource] = useState(false);
    const [processedEligibilityData, setProcessedEligibilityData] = useState([]);
    const [seekQHPApplicants, setSeekQHPApplicants] = useState({});
    const [seeksQHPSubmittedNames, setSeeksQHPSubmittedNames] = useState(false);
    const [fullDeterminationSubmitted, setFullDeterminationSubmitted] = useState(false);
    const [isMaintainFATextVisible, setIsMaintainFATextVisible] = useState(false);
    const [alertType, setAlertType] = useState(state?.prevLocation === configs.routes.SSAP ? ELIGIBILITY_ALERT_TYPES.SSAP_SUBMISSION : null);
    const { isEligibilityOverviewSummaryHeaderHidden } = useStateCode();

    useEffect(() => {
        getEnv();
    }, []);

    useEffect(() => {
        // There are 4 entry points for new eligibility results screen.
        // 1. - Left Nav. For this case no encryptedCaseNumber should ne passed.
        // 2. - MP:Dashboard. Latest encryptedCaseNumber should be passed, based on active tab.
        // 3. - MP:Applications. encryptedCaseNumber will be passed in a state object as part of history.push
        // 4. - SSAP. encryptedCaseNumber will be passed in a state object as part of history.push

        let encryptedCaseNumberParam = '';

        if (!seeksQHPSubmittedNames) {
            if ([configs.routes.INDPORTAL, configs.routes.APPLICATIONS, configs.routes.SSAP].includes(state?.prevLocation)) {
                encryptedCaseNumberParam = state?.encryptedCaseNumber;
            }
        }

        const ENDPOINT = configs.endpoints.ELIGIBILITY_RESULTS_NEW.processReplacements({ encryptedCaseNumber: encryptedCaseNumberParam });

        const onVerificationPendingFinishedCb = () => executeCallbacks([
            () => fetchEligibilityResults(ENDPOINT),
            () => {
                const {
                    encryptedCaseNumber,
                    prevLocation
                } = preventerTools.provideObject(state);

                if(prevLocation === configs.routes.SSAP && encryptedCaseNumber) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    getEnvironment();
                }
            }
        ]) ;

        if (delayPopup) {
            checkVerificationPending(onVerificationPendingFinishedCb);
        } else {
            onVerificationPendingFinishedCb();
        }

    }, [seeksQHPSubmittedNames]);

    useEffect(() => {
        setAllowedAppStatus([APP_STATUSES.EN, APP_STATUSES.ER, APP_STATUSES.PN].includes(applicationStatus));
        setIsAllowedAppSource(!DISALLOWED_APP_SOURCES.includes(source));
        setSeekQHPApplicants(initSeekQHPApplicantsState(applicants));
        setProcessedEligibilityData(processEligibilityResults(applicants, aptc, stateSubsidy, stateSubsidyEnabled));
    }, [eligibilityResults]);

    useEffect(() => {
        if (!fullDeterminationRequested && encryptedCaseNumber && requestFullDetermination === FULL_DETERMINATION_CONFIG.ON_APP_SUBMISSION_ONLY) {
            setFullDetermination(configs.endpoints.SET_FULL_DETERMINATION, { caseNumber: encryptedCaseNumber });
        }
    }, [encryptedCaseNumber]);


    /**
     * Helper method to get applicant's name by guid.
     * @param guid
     * @returns {*}
     */
    const getNameByGUID = (guid) => {
        for (let applicant of processedEligibilityData) {
            if (applicant.guid === guid) {
                return applicant.name.trim();
            }
        }
    };

    /**
     * Returns alert content by type.
     * @param alertType
     * @returns {*}
     */
    const getAlertContent = (alertType) => {
        let result = '';
        switch (alertType) {
            case ELIGIBILITY_ALERT_TYPES.SSAP_SUBMISSION:
                result = <FormattedMessage id='eligibility_results_alert_1' values={{ exchangeName: exchangeName }}/>;
                break;

            case ELIGIBILITY_ALERT_TYPES.SAVE_SEEKS_QHP:
                result = <FormattedMessage id='eligibility_results_alert_2' values={{ names: seeksQHPSubmittedNames }}/>;
                break;

            case (ELIGIBILITY_ALERT_TYPES.REQUEST_DETERMINATION):
                result = <FormattedMessage id='eligibility_results_alert_3'/>;
                break;

            default:
                result = false;
        }

        return result;
    };

    /**
     * Returns btn's text and cb for eligibility details section.
     * @returns {{btnCb: btnCb, btnText: *}}
     */
    const getBtnData = () => {
        return {
            btnText: <FormattedMessage id='button.go_to_dashboard'/>,
            btnCb: () => {
                navigate(configs.routes.INDPORTAL);
            }
        };
    };

    /**
     * EligibilityDetails: Callback for checkbox.
     * @param e
     * @param applicantGUID
     */
    const wantsToEnrollCheckboxCb = (e, applicantGUID) => {
        setSeekQHPApplicants({ ...seekQHPApplicants, [applicantGUID]: e.currentTarget.checked });
    };

    /**
     * Helper method to group members who succefully submitted seek qhp changes. (The result should be rendered in the Alert block)
     * @param applicantsResponse
     * @returns {*}
     */
    const getSeeksQHPSubmittedNames = (applicantsResponse) => {
        return applicantsResponse.map(applicant => {
            return applicant.applicantName;
        }).join(', ');
    };

    /**
     * Returns a list of applicants who wants to enroll in QHP.
     * @returns {*}
     */
    const constructSeeksQHPPayloadRequest = () => {
        const applicantsPayload = [];

        for (let guid in seekQHPApplicants) {
            if (seekQHPApplicants[guid]) {
                applicantsPayload.push({
                    seekQhpIndicator: true,
                    applicantGUID: guid,
                    applicantName: getNameByGUID(guid)
                });
            }
        }
        return applicantsPayload.length > 0 ? applicantsPayload : false;
    };

    /**
     * Main logic for SeeksQHP component.
     * @returns {{columns: *}}
     * @param data
     */
    const getSeeksQHP = data => {

        const SEEKS_QHP_KEYS = {
            ASSESSED_MEDICAID: 'eligibility_results.seeksQHP.potentially_medicaid',
            ASSESSED_CHIP: 'eligibility_results.seeksQHP.potentially_chip'
        };

        const SEEKS_QHP_HEADERS = [
            { id: 'name', text: 'eligibility_results.details.table_header1' },
            { id: 'wantsToEnroll', text: 'eligibility_results.seeks_qhp.table_header' },
        ];

        const seeksQHP = { columns: getFormattedHeaders(SEEKS_QHP_HEADERS) };

        seeksQHP.data = data.reduce((acc, applicant) => {
            const { assessedMedicaidMAGIEligible, assessedChipEligible, exchangeEligible } = applicant.rawData;

            if ((assessedMedicaidMAGIEligible || assessedChipEligible) && !exchangeEligible) {
                acc.push({
                    name:
                        <Fragment>
                            <p className='text-bold'>{applicant.name}</p>
                            <p className='seeks-QHP__sign'><FormattedMessage
                                id={assessedMedicaidMAGIEligible ? SEEKS_QHP_KEYS.ASSESSED_MEDICAID : SEEKS_QHP_KEYS.ASSESSED_CHIP}/>
                            </p>
                        </Fragment>,
                    wantsToEnroll: <input id={applicant.guid} type="checkbox" checked={seekQHPApplicants[applicant.guid]}
                        onChange={(e) => wantsToEnrollCheckboxCb(e, applicant.guid)}/>
                });
            }
            return acc;

        }, []);

        return seeksQHP;
    };


    /**
     * SeeksQHP: Callback to save members who want to enroll in a plan.
     */
    const saveSeeksQHPChangesCb = () => {
        const payload = {
            encryptedCaseNumber: encryptedCaseNumber,
            applicants: constructSeeksQHPPayloadRequest()
        };

        saveSeekQHPChanges(configs.endpoints.SAVE_SEEKS_QHP_CHANGES, payload).then(response => {
            if (response && response.status === 'SUCCESS') {
                setSeeksQHPSubmittedNames(getSeeksQHPSubmittedNames(response.applicants));
                setAlertType(ELIGIBILITY_ALERT_TYPES.SAVE_SEEKS_QHP);
            }
        });
    };

    /**
     * RequestFullDetermination: Callback to request full determination.
     */
    const submitDeterminationCb = () => {
        const payload = { caseNumber: encryptedCaseNumber };

        postFullDetermination(configs.endpoints.REQUEST_FULL_DETERMINATION, payload).then((response) => {
            if (response && response.status === 'SUCCESS') {
                setAlertType(ELIGIBILITY_ALERT_TYPES.REQUEST_DETERMINATION);
                setFullDeterminationSubmitted(true);
            } else {
                console.error(response && response.errorMessage);
            }
        });
    };


    const checkVisibilityFullDetermination = processedEligibilityData => {
        if (requestFullDetermination === FULL_DETERMINATION_CONFIG.OFF) {
            return false;
        } else {
            const { ADMIN, L1_CSR, L2_CSR, SUPERVISOR } = ALL_USER_ROLES;
            const superUser = [ADMIN, L1_CSR, L2_CSR, SUPERVISOR].includes(role);
            const eligibleMemberCheck = processedEligibilityData && processedEligibilityData.some(applicant => {
                const {
                    exchangeEligible,
                    assessedMedicaidMAGIEligible,
                    assessedMedicaidNonMAGIEligible,
                    assessedChipEligible,
                    medicaidMAGIEligible,
                    medicaidNonMAGIEligible,
                    chipEligible
                } = applicant.rawData;

                return exchangeEligible && !assessedMedicaidMAGIEligible && !assessedMedicaidNonMAGIEligible && !assessedChipEligible &&
                    !medicaidMAGIEligible && !medicaidNonMAGIEligible && !chipEligible;
            });

            return isState.NJ && superUser ? eligibleMemberCheck : !fullDeterminationRequested && eligibleMemberCheck;
        }
    };

    const checkVisibilitySeeksQHP = processedEligibilityData => {
        return processedEligibilityData && processedEligibilityData.some(applicant => {
            const { exchangeEligible, assessedMedicaidMAGIEligible, assessedChipEligible } = applicant.rawData;
            return !exchangeEligible && (assessedMedicaidMAGIEligible || assessedChipEligible);
        });
    };

    const getEstateRecoveryLink = (stateCode) => {
        const externalEndpoints = configs.externalEndpoints[`${stateCode}_LINKS`];
        const estateRecoveryLink = externalEndpoints && externalEndpoints.ESTATE_RECOVERY;
        return (
            <a
                className="usa-link--external"
                href={estateRecoveryLink}
                target="_blank"
                rel="noopener noreferrer"
            >
                <FormattedMessage id='ssap.sign_and_review.estate_recovery_link'/>
            </a>
        );
    };

    function initSeekQHPApplicantsState(applicants) {

        const initialState = {};
        applicants && applicants.forEach(applicant => {
            const { guid, exchangeEligible, assessedMedicaidMAGIEligible, assessedChipEligible } = applicant;
            if (!exchangeEligible && (assessedMedicaidMAGIEligible || assessedChipEligible)) {
                initialState[guid] = false;
            }
        });
        return initialState;
    }

    /**
     * Main logical method - constructs general applicant's eligibility results per each eligibility rules.
     * @param applicants
     * @param aptc
     * @param stateSubsidy
     * @returns {*}
     */
    function processEligibilityResults(applicants, aptc, stateSubsidy ) {

        return applicants && applicants.length > 0 && applicants.reduce((acc, applicant) => {
            const {
                guid,
                firstName,
                lastName,
                middleName,
                nameSuffix,
                exchangeEligible,
                aptcEligible,
                csrEligible,
                stateSubsidyEligible,
                stateSubsidy2Eligible,
                stateSubsidy3Eligible,
                assessedChipEligible,
                assessedMedicaidMAGIEligible,
                assessedMedicaidNonMAGIEligible,
                medicaidMAGIEligible,
                medicaidNonMAGIEligible,
                chipEligible,
                seekingCoverage,
                emergencyMedicaidEligible = false
            } = applicant;

            const applicantInfo = {
                guid: guid,
                name: `${firstName} ${middleName} ${lastName} ${nameSuffix}`,
                eligibilities: [],
                rawData: applicant
            };

            if (exchangeEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.QHP);

            if (aptc !== null && aptcEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.APTC);

            if (stateSubsidy !== null && stateSubsidyEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY);

            if (stateSubsidy2Eligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY_2);

            if (stateSubsidy3Eligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY_3);

            if (csrEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.CSR);

            if (assessedMedicaidMAGIEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.ASSESSED_MAGI_MEDICAID);

            if (assessedMedicaidNonMAGIEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.MEDICAID_REFERRAL);

            if (assessedChipEligible) {
                setAdditionalNoteVisibility(true);
                applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.ASSESSED_CHIP);
            }

            if (assessedMedicaidMAGIEligible || assessedMedicaidNonMAGIEligible) {
                setAdditionalNoteVisibility(true);
                applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.ASSESSED_MEDICAID);
            }

            if (medicaidMAGIEligible || medicaidNonMAGIEligible || chipEligible) {
                applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.MEDICAID_OR_CHIP);
            }

            if (exchangeEligible && aptcEligible && (assessedChipEligible || assessedMedicaidMAGIEligible || assessedMedicaidNonMAGIEligible)) {
                setIsMaintainFATextVisible(true);
            }

            if ( isState.ID || isState.GA) {
                if ( !exchangeEligible && seekingCoverage) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.NOT_QHP);
            } else {
                if ( !exchangeEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.NOT_QHP);
            }

            if (!seekingCoverage) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.NOT_SEEKING);

            if (emergencyMedicaidEligible) applicantInfo.eligibilities.push(ALL_ELIGIBILITY_TYPES.POTENTIALLY_EMERGENCY_MEDICAID_ELIGIBLE);

            acc.push(applicantInfo);

            return acc;

        }, []);
    }

    return (
        
        eligibilityResults &&
        (eligibilityResults.status === 'FAILURE' ||
            (eligibilityResults.applicants && !eligibilityResults.applicants.length)
        ) ?
            <FormattedMessage id="eligibility_results.overview.no_app" tagName='p'/> :

            processedEligibilityData && processedEligibilityData.length > 0 ?
                <div id="eligibilityResults" className="usa-layout-docs gi-main-section eligibility-results">
                    {alertType &&
                        <div className="eligibility__alert-wrap">
                            <AlertMessage
                                type="success"
                                content={getAlertContent(alertType)}
                            />
                        </div>
                    }

                    <div className='eligibility__main gi-panel gi-panel__padding-lg'>
                        <EligibilityOverviewContainer
                            processedEligibilityData={processedEligibilityData}
                            additionalNote={additionalNote}
                            showSummaryHeader={!isEligibilityOverviewSummaryHeaderHidden}
                            isMaintainFATextVisible={isMaintainFATextVisible}
                        />

                        <EligibilityDetailsContainer
                            getBtnData={getBtnData}
                            processedEligibilityData={processedEligibilityData}
                            showActionNeeded={!isState.MN}
                            stateCode={stateCode}
                        />

                    </div>
                    <hr/>

                    {isLatestApplication && isAllowedAppSource && allowedAppStatus && isFinancial &&
                        (checkVisibilitySeeksQHP(processedEligibilityData) || checkVisibilityFullDetermination(processedEligibilityData)) &&

                        <div className="eligibility__optional-actions">
                            <h2 className="eligibility__header--optional">
                                <FormattedMessage id="eligibility_results.optional_actions"/>
                            </h2>

                            <SeeksQHP
                                seeksQHP={getSeeksQHP(processedEligibilityData)}
                                collapsed={showSeekQHPCollapsed}
                                disableSaveChangesBtn={!constructSeeksQHPPayloadRequest()}
                                onSaveChangesCb={saveSeeksQHPChangesCb}
                                additionalNote={isState.NV || isState.VA}
                                exchangeName={exchangeName}
                            />

                            {
                                checkVisibilityFullDetermination(processedEligibilityData) &&
                                <RequestFullDetermination
                                    toggleEnabled={isState.NJ}
                                    onSubmitCb={submitDeterminationCb}
                                    fullDeterminationSubmitted={fullDeterminationSubmitted}
                                    link={getEstateRecoveryLink(stateCode)}
                                    exchangeName={exchangeName}
                                />
                            }
                        </div>
                    }
                </div> : null
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    dashboardModels: state.individualPortal.dashboard.dashboardModels,
    activeTab: state.individualPortal.dashboard.activeTab,
    eligibilityResults: state.individualPortal.eligibilityResults
});

export default connect(mapStateToProps, {
    checkVerificationPending,
    fetchEligibilityResults,
    getEnvironment,
    saveSeekQHPChanges,
    postFullDetermination,
    setFullDetermination
})(EligibilityResultsContainer);